import { useEffect, useMemo } from 'react';
import { shallowEqual } from 'react-redux';
import dayjs from 'dayjs';
import { useAppDispatch, useAppSelector } from '../hooks';
import { RootState } from '../store';
import { selectIsAuthenticated } from '../containers/authSlice';
import {
	selectAllEntryProperties,
	selectAllPropertyDefinitions,
	selectAllRepositories,
	selectAllRepositoryEntries,
} from '../containers/contentSlice';
import { repositoryEntriesApi, type RepositoryEntriesRowDto } from '../services/repositoryEntries';
import { populateRepositoryEntriesForTable, sortEntryByRank } from '../common/util';
import { matchesResolvedDataFilter } from '../common/offlineDataFilter';
import { PropertyDefinition, Repository } from '../types';
import { useSharedView } from './useSharedView';
import { SharedRepositorySourceDto } from '../services/sharedCollections';

export interface TimelineSourceSpec {
	repositoryId: string;
	/** Day-axis field, matched by id when set, otherwise by `datePropertySlug`. */
	datePropertyId?: string;
	datePropertySlug?: string;
}

export interface TimelineSource {
	repository: Repository | null;
	propertyDefinitions: PropertyDefinition[];
	dateProperty: PropertyDefinition | undefined;
	/** Only entries with a valid date in `dateProperty`. */
	rows: RepositoryEntriesRowDto[];
}

/**
 * Loads each collection plotted on a timeline. Authenticated, it holds one `getRepositoryById` and one
 * `getRepositoryEntriesByFilter` subscription per collection, dispatched directly since the generated
 * query hooks can't be called a variable number of times. In a shared view the collections come from the
 * share link's response (`sharedSources`) instead.
 */
export function useTimelineSources(
	specs: TimelineSourceSpec[],
	{ dataFilter, includeArchived, sharedSources }: { dataFilter?: string; includeArchived: boolean; sharedSources?: SharedRepositorySourceDto[] },
): { sources: TimelineSource[]; isLoading: boolean } {
	const dispatch = useAppDispatch();
	const isAuthenticated = useAppSelector(selectIsAuthenticated);
	const { isSharedView } = useSharedView();
	const isCloudData = isAuthenticated && !isSharedView;
	const allRepositories = useAppSelector(selectAllRepositories);
	const allPropertyDefinitions = useAppSelector(selectAllPropertyDefinitions);
	const allRepositoryEntries = useAppSelector(selectAllRepositoryEntries);
	const allEntryProperties = useAppSelector(selectAllEntryProperties);

	const repositoryIdsKey = specs.map((spec) => spec.repositoryId).join(',');
	const repositoryIds = useMemo(() => (repositoryIdsKey ? repositoryIdsKey.split(',') : []), [repositoryIdsKey]);
	const entriesArgs = useMemo(
		() => repositoryIds.map((repositoryId) => ({ repositoryId, dataFilter, includeArchived, sortDir: 'asc' as const })),
		[repositoryIds, dataFilter, includeArchived],
	);

	useEffect(() => {
		if (!isCloudData) return undefined;
		const subscriptions = [
			...repositoryIds.map((repositoryId) => dispatch(repositoryEntriesApi.endpoints.getRepositoryById.initiate(repositoryId))),
			...entriesArgs.map((arg) => dispatch(repositoryEntriesApi.endpoints.getRepositoryEntriesByFilter.initiate(arg))),
		];
		return () => subscriptions.forEach((subscription) => subscription.unsubscribe());
	}, [dispatch, isCloudData, repositoryIds, entriesArgs]);

	const selectResults = useMemo(() => {
		const repositorySelectors = repositoryIds.map((repositoryId) => repositoryEntriesApi.endpoints.getRepositoryById.select(repositoryId));
		const entriesSelectors = entriesArgs.map((arg) => repositoryEntriesApi.endpoints.getRepositoryEntriesByFilter.select(arg));
		return (state: RootState) => ({
			repositoryResults: repositorySelectors.map((select) => select(state)),
			entriesResults: entriesSelectors.map((select) => select(state)),
		});
	}, [repositoryIds, entriesArgs]);
	const repositoryResults = useAppSelector((state) => selectResults(state).repositoryResults, shallowEqual);
	const entriesResults = useAppSelector((state) => selectResults(state).entriesResults, shallowEqual);

	const sources = useMemo(
		() => specs.map((spec, index): TimelineSource => {
			const sharedSource = sharedSources?.find((source) => source.repository.id === spec.repositoryId);
			const repository = isSharedView
				? (sharedSource?.repository ?? null)
				: isCloudData
					? (repositoryResults[index]?.data?.repository ?? null)
					: (allRepositories.find((r) => r.id === spec.repositoryId) ?? null);
			const rawPropertyDefinitions = isSharedView
				? (sharedSource?.propertyDefinitions ?? [])
				: isCloudData
					? (repositoryResults[index]?.data?.propertyDefinitions ?? [])
					: allPropertyDefinitions.filter((def) => def.parentId === spec.repositoryId);
			const propertyDefinitions = [...rawPropertyDefinitions].sort(sortEntryByRank) as PropertyDefinition[];
			const dateProperty = propertyDefinitions.find((def) => (spec.datePropertyId ? def.id === spec.datePropertyId : def.slug === spec.datePropertySlug));
			if (!dateProperty) {
				return { repository, propertyDefinitions, dateProperty, rows: [] };
			}
			let allRows: RepositoryEntriesRowDto[] = [];
			if (isSharedView) {
				allRows = sharedSource?.entries.rows ?? [];
			} else if (isCloudData) {
				allRows = entriesResults[index]?.data?.rows ?? [];
			} else if (repository) {
				allRows = populateRepositoryEntriesForTable(repository, allRepositoryEntries, allPropertyDefinitions, allEntryProperties)
					.filter((row) => matchesResolvedDataFilter(row, dataFilter))
					.filter((row) => includeArchived || !row.isArchived)
					.sort(sortEntryByRank);
			}
			const rows = allRows.filter((row) => {
				const raw = row.data?.[dateProperty.slug];
				return typeof raw === 'string' && Boolean(raw) && dayjs(raw).isValid();
			});
			return { repository, propertyDefinitions, dateProperty, rows };
		}),
		[specs, isSharedView, sharedSources, isCloudData, repositoryResults, entriesResults, allRepositories, allPropertyDefinitions, allRepositoryEntries, allEntryProperties, dataFilter, includeArchived],
	);

	/** Only a first load counts — a background refetch (e.g. after creating an entry) keeps showing the cached rows. */
	const isLoading = isCloudData
		&& [...repositoryResults, ...entriesResults].some((result) => result.data === undefined && !result.isError);

	return { sources, isLoading };
}
